import {useCallback, useEffect, useState} from 'react';

import {Button, CheckBox, Modal, SpinningLoader} from '@/components';
import type {BusinessCountry, BusinessCountryFlags} from '@/types';
import {logger} from '@/utils/logger';

import {
  BUSINESS_COUNTRY_ACCOUNT_FLAGS,
  BUSINESS_COUNTRY_CORRIDOR_FLAGS,
  BUSINESS_SURFACES,
  type BusinessCountryCorridorFlagKey,
  type BusinessSurface,
  resolveSurfaceFlag,
  unsetCorridorFlagValue,
} from './businessCountryFlags';

type BusinessCountryFlagsModalProps = {
  onClose: VoidFunction;
  showModal: boolean;
  handleSave: (id: string, body: Partial<BusinessCountry>) => Promise<void>;
  item: BusinessCountry;
};

/**
 * The corridor switches are held once per surface, so every row here is two
 * checkboxes: the dashboard and the merchant API (which hosted checkout runs
 * on). Closing a corridor to direct API traffic should not close it for
 * someone working in the dashboard, and the two were a single switch until
 * this screen split them.
 *
 * The form seeds with only the switches a surface actually stores, and a box
 * whose switch is unset shows what the server does with it
 * (`unsetCorridorFlagValue`): allowed, or not deactivated. An unset switch stays
 * unset on save unless someone clicks it. Seeding unset as `false` used to turn
 * every untouched capability on both surfaces into an explicit `false` on the
 * first save, closing corridors nobody meant to close.
 */
const BusinessCountryFlagsModal = ({
  onClose,
  showModal,
  handleSave,
  item,
}: BusinessCountryFlagsModalProps) => {
  const [formValues, setFormValues] = useState<BusinessCountryFlags>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const flags = item?.meta?.flags;
    setFormValues({
      canSignup: flags?.canSignup,
      ...Object.fromEntries(
        BUSINESS_SURFACES.map(({key: surface}) => [
          surface,
          Object.fromEntries(
            BUSINESS_COUNTRY_CORRIDOR_FLAGS.flatMap(({key}) => {
              const value = resolveSurfaceFlag(flags, surface, key);
              return value === undefined ? [] : [[key, value]];
            }),
          ),
        ]),
      ),
    });
  }, [item?.meta?.flags]);

  const setSurfaceFlag = useCallback(
    (
      surface: BusinessSurface,
      key: BusinessCountryCorridorFlagKey,
      checked: boolean,
    ) =>
      setFormValues((prev) => ({
        ...prev,
        [surface]: {...prev?.[surface], [key]: checked},
      })),
    [],
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      setIsLoading(true);
      try {
        // The PATCH is a plain `$set` on `meta`, so the whole subtree the
        // screen read is echoed back with only the flags replaced.
        //
        // `flags` is rebuilt from the form rather than spread over the stored
        // object, so the flat corridor switches the server resolves onto the
        // response do not get echoed back into a write. They are accepted by
        // the request schema and dropped by the Mongoose schema either way,
        // but not sending them is what actually keeps a response shape from
        // being mistaken for a record shape.
        await handleSave(String(item?.id), {
          meta: {...item?.meta, flags: formValues},
        });
        onClose();
      } catch (error) {
        // `handleSave` already reports its own failures with a toast, so this
        // only fires if that reporting itself threw. The modal deliberately
        // stays open either way, so the edit is not silently discarded.
        logger.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [formValues, handleSave, item, onClose],
  );

  return (
    <Modal
      isOpen={showModal}
      onClose={onClose}
      width="w-11/12 md:w-7/12 lg:w-6/12 xl:w-5/12 px-4 sm:px-0"
      title={`${item?.name ?? ''} Business Onboarding`}
      closeOnOutsideClick
    >
      <div className="mx-5">
        {isLoading ? (
          <div className="flex w-full h-full justify-center items-center my-5">
            <SpinningLoader />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col mt-5">
            {BUSINESS_COUNTRY_CORRIDOR_FLAGS.map(({key, label, hint}) => (
              <div key={key} className="mb-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-100">
                  {label}
                </p>
                <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                  {hint}
                </p>
                <div className="flex flex-wrap gap-x-8 ml-1">
                  {BUSINESS_SURFACES.map(
                    ({key: surface, label: surfaceLabel}) => (
                      <CheckBox
                        key={surface}
                        id={`${surface}-${key}`}
                        label={surfaceLabel}
                        // The visible label is just the surface, because the
                        // switch it belongs to is the heading above. That
                        // leaves eight boxes sharing two labels, so the
                        // accessible name carries both: without it a screen
                        // reader hears "Dashboard" four times with nothing to
                        // tell them apart.
                        aria-label={`${label} (${surfaceLabel})`}
                        checked={
                          formValues?.[surface]?.[key] ??
                          unsetCorridorFlagValue(key)
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setSurfaceFlag(surface, key, e.target.checked)
                        }
                      />
                    ),
                  )}
                </div>
              </div>
            ))}

            {BUSINESS_COUNTRY_ACCOUNT_FLAGS.map(({key, label, hint}) => (
              <div key={key}>
                <CheckBox
                  id={key}
                  label={label}
                  checked={formValues?.[key] === true}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormValues((prev) => ({
                      ...prev,
                      [key]: e.target.checked,
                    }))
                  }
                />
                <p className="ml-8 -mt-2 mb-3 text-xs text-slate-500 dark:text-slate-400">
                  {hint}
                </p>
              </div>
            ))}

            <div className="flex w-full justify-end space-x-2 py-5">
              {/* Explicitly type="button": inside a <form> the default is
                  submit, so Cancel would have saved the very edit it is meant
                  to abandon. */}
              <Button type="button" onClick={onClose} variant="outline">
                Cancel
              </Button>
              <Button disabled={isLoading} type="submit" variant="solid">
                Submit
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default BusinessCountryFlagsModal;
